/**
 * Input mask — 03-components/15-form-inputs/input-mask.js · OSCloud DS 1.1.154
 */
import { ref, type Ref } from 'vue'

export const INPUT_MASK_PRESETS = {
  card: '#### #### #### ####',
  expiry: '## / ##',
  personnummer: '########-####',
  orgnr: '######-####',
  postcode: '### ##',
} as const

export type InputMaskPreset = keyof typeof INPUT_MASK_PRESETS

export interface InputMaskState {
  value: string
  raw: string
  complete: boolean
}

type Token = { test: RegExp; lit?: undefined } | { lit: string; test?: undefined }
type Kind = 's' | 'l'

interface ParsedMask {
  tokens: Token[]
  prefix: string
  slots: RegExp[]
  prefixCore: string
}

const TESTS: Record<string, RegExp> = {
  '#': /^\d$/,
  A: /^\p{L}$/u,
  '*': /^[\p{L}\d]$/u,
}

const LETTER_OR_DIGIT = /[\p{L}\d]/u

const cache = new Map<string, ParsedMask>()

function parse(source: string): ParsedMask {
  const pattern = INPUT_MASK_PRESETS[source as InputMaskPreset] ?? source
  const cached = cache.get(pattern)
  if (cached) return cached
  const tokens: Token[] = []
  for (let i = 0; i < pattern.length; i++) {
    const ch = pattern[i]
    if (ch === '\\' && i + 1 < pattern.length) {
      tokens.push({ lit: pattern[++i] })
      continue
    }
    tokens.push(TESTS[ch] ? { test: TESTS[ch] } : { lit: ch })
  }
  let prefix = ''
  for (let j = 0; j < tokens.length && tokens[j].lit !== undefined; j++) prefix += tokens[j].lit
  const mask: ParsedMask = {
    tokens,
    prefix,
    slots: tokens.flatMap(t => (t.test ? [t.test] : [])),
    prefixCore: prefix
      .split('')
      .filter(c => LETTER_OR_DIGIT.test(c))
      .join(''),
  }
  cache.set(pattern, mask)
  return mask
}

function stripPrefix(mask: ParsedMask, str: string): string {
  const p = mask.prefix
  if (!p) return str
  if (str.indexOf(p) === 0) return str.slice(p.length)
  if (p.indexOf(str) === 0) return ''
  if (!mask.prefixCore || str[0] !== p[0]) return str
  let k = 0
  let i = 1
  for (; i < str.length && k < mask.prefixCore.length; i++) {
    if (str[i] === mask.prefixCore[k]) k++
    else if (LETTER_OR_DIGIT.test(str[i])) return str
  }
  return k === mask.prefixCore.length ? str.slice(i) : str
}

function extract(mask: ParsedMask, str: string): string[] {
  const s = stripPrefix(mask, str)
  const raw: string[] = []
  for (let i = 0; i < s.length && raw.length < mask.slots.length; i++) {
    if (mask.slots[raw.length].test(s[i])) raw.push(s[i])
  }
  return raw
}

function format(mask: ParsedMask, raw: string[], showPrefix: boolean): { value: string; kinds: Kind[] } {
  let value = ''
  const kinds: Kind[] = []
  let r = 0
  for (let i = 0; i < mask.tokens.length; i++) {
    const t = mask.tokens[i]
    if (t.test) {
      if (r >= raw.length) break
      value += raw[r++]
      kinds.push('s')
    } else {
      const isPrefix = i < mask.prefix.length
      if (isPrefix ? !(showPrefix || raw.length) : r >= raw.length) {
        if (!isPrefix) break
        continue
      }
      value += t.lit
      kinds.push('l')
    }
  }
  return { value, kinds }
}

function caretAfter(kinds: Kind[], n: number): number {
  if (n === 0) {
    let p = 0
    while (p < kinds.length && kinds[p] === 'l') p++
    return p
  }
  for (let i = 0, seen = 0; i < kinds.length; i++) {
    if (kinds[i] === 's' && ++seen === n) return i + 1
  }
  return kinds.length
}

function slotIndexAt(kinds: Kind[], pos: number): number {
  let n = 0
  for (let i = 0; i < pos; i++) if (kinds[i] === 's') n++
  return n
}

export function formatWithMask(pattern: string, value: string): string {
  const mask = parse(pattern)
  return format(mask, extract(mask, value), false).value
}

export function useInputMask(options: {
  input: Ref<HTMLInputElement | null>
  mask: () => string
  validate: () => boolean
  onChange: (state: InputMaskState) => void
}) {
  const raw = ref('')
  const complete = ref(false)
  const invalid = ref(false)

  const isMasked = () => Boolean(options.mask()) && /^(text|tel|search)$/.test(options.input.value?.type ?? '')

  function apply(el: HTMLInputElement, chars: string[], caretRaw?: number) {
    const mask = parse(options.mask())
    const focused = document.activeElement === el
    const out = format(mask, chars, focused)
    complete.value = chars.length === mask.slots.length
    raw.value = chars.join('')
    if (el.value !== out.value) el.value = out.value
    if (focused && caretRaw !== undefined) {
      const pos = caretAfter(out.kinds, Math.min(caretRaw, chars.length))
      el.setSelectionRange(pos, pos)
    }
    if (options.validate() && complete.value && invalid.value) invalid.value = false
    options.onChange({ value: out.value, raw: raw.value, complete: complete.value })
  }

  function refresh() {
    const el = options.input.value
    if (!el || !isMasked()) return
    apply(el, extract(parse(options.mask()), el.value))
  }

  function onInput(e: Event) {
    const el = options.input.value
    if (!el || !isMasked() || (e as InputEvent).isComposing) return
    const mask = parse(options.mask())
    const caret = el.selectionStart ?? el.value.length
    const before = extract(mask, el.value.slice(0, caret)).length
    apply(el, extract(mask, el.value), before)
  }

  function onBeforeInput(e: InputEvent) {
    const el = options.input.value
    if (!el || !isMasked() || e.isComposing) return
    const back = e.inputType === 'deleteContentBackward'
    const fwd = e.inputType === 'deleteContentForward'
    if (!back && !fwd) return
    const start = el.selectionStart ?? 0
    if (start !== el.selectionEnd) return
    const mask = parse(options.mask())
    const chars = extract(mask, el.value)
    const kinds = format(mask, chars, true).kinds
    let p = back ? start - 1 : start
    if (back) {
      while (p >= 0 && kinds[p] === 'l') p--
      if (p < 0) {
        e.preventDefault()
        return
      }
    } else {
      while (p < kinds.length && kinds[p] === 'l') p++
      if (p >= kinds.length) return
    }
    if (p === (back ? start - 1 : start)) return
    e.preventDefault()
    const i = slotIndexAt(kinds, p)
    chars.splice(i, 1)
    apply(el, chars, i)
  }

  function onFocus() {
    const el = options.input.value
    if (!el || !isMasked()) return
    const mask = parse(options.mask())
    if (!mask.prefix) return
    const chars = extract(mask, el.value)
    apply(el, chars, chars.length)
  }

  function onBlur() {
    const el = options.input.value
    if (!el || !isMasked()) return
    const mask = parse(options.mask())
    const chars = extract(mask, el.value)
    setTimeout(() => {
      apply(el, chars)
      if (options.validate()) invalid.value = chars.length > 0 && chars.length < mask.slots.length
    }, 0)
  }

  return { raw, complete, invalid, refresh, onInput, onBeforeInput, onFocus, onBlur }
}
